import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Stack } from '@mui/material';
import { useState } from 'react';

import { Text, api, isDefined } from 'common';

import { IteratorActionBlockFormFields } from './ActionBlockFormFields';
import { getActionBlockConfig } from './defaultConfigurations';
import { BlockValueTemplateField } from '../../../ConfigFields/CodeMirror/FlowTemplate';
import { BlockDurationField } from '../../../ConfigFields/Duration';
import { BlockSelectField } from '../../../ConfigFields/SelectField';
import { useSelectedNodeField } from '../../../ConfigFields/useSelectedNodeField';

import DurationUnits = api.FlowDefinition.DurationUnits;

const actionOptions = api.FlowDefinition.AllowedActionBlockSchema.options.map((action) => ({
  label: action.shape.type.value,
  value: action.shape.type.value,
}));

enum Section {
  Action = 'Action',
  AdvancedSettings = 'AdvancedSettings',
}

export function IteratorBlockFormFields() {
  const { value: blockType } =
    useSelectedNodeField<api.FlowDefinition.BlockType>('config.action.type');
  const { setValue: setActionConfig } = useSelectedNodeField('config.action.config');
  const [expandedSection, setExpandedSection] = useState<Section | null>(Section.Action);

  return (
    <Stack gap={2}>
      {/* List to Iterate at the top */}
      <BlockValueTemplateField
        fieldName="config.items"
        label="List to iterate"
        helperText="The list of items to iterate over"
      />

      <Stack>
        {/* Action Accordion */}
        <Accordion
          variant="elevation"
          expanded={expandedSection === Section.Action}
          onChange={() =>
            setExpandedSection(expandedSection === Section.Action ? null : Section.Action)
          }
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="action-content">
            <Text variant="heading5" bold>
              Action Block
            </Text>
          </AccordionSummary>
          <AccordionDetails>
            <Stack gap={2}>
              <BlockSelectField
                fieldName="config.action.type"
                label="Block to execute"
                options={actionOptions}
                onChange={(event) =>
                  isDefined(event.target.value)
                    ? setActionConfig(getActionBlockConfig(event.target.value))
                    : setActionConfig(undefined)
                }
                helperText="The action to execute for each entry in the list, in the form of another block"
              />
              {blockType && (
                <Box sx={{ border: 1, borderColor: 'divider', padding: 2, borderRadius: 1 }}>
                  <IteratorActionBlockFormFields blockType={blockType} />
                </Box>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Advanced Settings Accordion */}
        <Accordion
          variant="elevation"
          expanded={expandedSection === Section.AdvancedSettings}
          onChange={() =>
            setExpandedSection(
              expandedSection === Section.AdvancedSettings ? null : Section.AdvancedSettings
            )
          }
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="advanced-settings-content"
          >
            <Text variant="heading5" bold>
              Advanced settings
            </Text>
          </AccordionSummary>
          <AccordionDetails>
            <Stack gap={2}>
              <BlockValueTemplateField
                fieldName="config.itemKey"
                label="Item key"
                helperText="The key to retrieve the unique identifier from the item in the list"
              />
              <BlockSelectField
                fieldName="config.errorResolution"
                label="Error resolution"
                options={Object.values(api.FlowDefinition.IteratorBlockErrorResolution).map(
                  (entry: api.FlowDefinition.IteratorBlockErrorResolution) => ({
                    label: entry,
                    value: entry,
                  })
                )}
                defaultValue={api.FlowDefinition.IteratorBlockErrorResolution.Continue}
                helperText="What to do if an error occurs during the iteration"
              />
              <BlockDurationField
                fieldName="config.iterationStepDuration"
                unitFieldNameSuffix="units"
                allowedUnits={[DurationUnits.Hours, DurationUnits.Minutes, DurationUnits.Seconds]}
                toggleLabel="Time interval"
                valueLabel="Time interval"
                valueHelperText="Duration to wait between processing items"
              />
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Stack>
    </Stack>
  );
}
